import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { resolveImageUrl } from "@/utils/imageUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import {
  Heart,
  Share2,
  Mail,
  Eye,
  Send,
  Loader2,
  Copy,
  Check,
  MessageCircle,

  ShoppingBag,
  ChevronDown,
  ChevronUp,
  Facebook,
  Twitter,
  Linkedin,
  Search,
  X,
  TrendingUp,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = window.location.origin;

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation(0.05);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ═══ Floating Hearts Animation ═══ */
function FloatingHearts({ show }) {
  if (!show) return null;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-red-500"
          initial={{
            x: Math.random() * 100 - 50 + 50 + "%",
            y: "80%",
            opacity: 1,
            scale: 0.5 + Math.random() * 0.8,
          }}
          animate={{
            y: "-20%",
            opacity: 0,
            x: `${Math.random() * 100 - 50 + 50}%`,
            rotate: Math.random() * 40 - 20,
          }}
          transition={{
            duration: 1.2 + Math.random() * 0.8,
            delay: i * 0.08,
            ease: "easeOut",
          }}
        >
          <Heart className="w-5 h-5 fill-red-500" />
        </motion.div>
      ))}
    </div>
  );
}

/* ═══ Post Card Component ═══ */
function PostCard({ post, settings, onLike, onInquiry, onShare, onImageClick }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showHearts, setShowHearts] = useState(false);
  const [likedPosts, setLikedPosts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("liked_posts") || "[]"); }
    catch { return []; }
  });

  const isLiked = likedPosts.includes(post.id);
  const DESC_LIMIT = 120;
  const isLong = post.description && post.description.length > DESC_LIMIT;

  const handleLike = () => {
    let updated;
    if (isLiked) {
      updated = likedPosts.filter(id => id !== post.id);
      onLike(post.id, "unlike");
    } else {
      updated = [...likedPosts, post.id];
      onLike(post.id, "like");
      setShowHearts(true);
      setTimeout(() => setShowHearts(false), 2000);
    }
    setLikedPosts(updated);
    localStorage.setItem("liked_posts", JSON.stringify(updated));
  };

  return (
    <motion.div
      className="group bg-white rounded-2xl border border-gray-100 overflow-hidden break-inside-avoid mb-5"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ boxShadow: "0 12px 40px rgba(0,0,0,0.08)" }}
      transition={{ duration: 0.3 }}
      layout
      data-testid={`post-card-${post.id}`}
    >
      {/* Image — natural aspect ratio */}
      <div className="relative overflow-hidden bg-gray-100">
        <FloatingHearts show={showHearts} />
        {!imageLoaded && (
          <div className="flex items-center justify-center py-20">
            <img src="/logo.png" alt="Great Messiah Publication" className="w-10 h-10 object-contain opacity-30 animate-pulse" />
          </div>
        )}
        <img
          src={resolveImageUrl(post.image_url)}
          alt={post.title}
          className={`w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-700 cursor-pointer ${imageLoaded ? "block" : "hidden"}`}
          onLoad={() => setImageLoaded(true)}
          onClick={() => onImageClick(post)}
          onDoubleClick={handleLike}
        />
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Action bar */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleLike}
              className="group/like flex items-center gap-1 transition-transform active:scale-90"
              data-testid={`like-btn-${post.id}`}
            >
              <Heart
                className={`w-6 h-6 transition-all duration-300 ${
                  isLiked
                    ? "fill-red-500 text-red-500 scale-110"
                    : "text-gray-600 hover:text-red-400"
                }`}
              />
            </button>
            <button
              onClick={() => onInquiry(post)}
              className="transition-transform active:scale-90"
              data-testid={`inquiry-btn-${post.id}`}
            >
              <Mail className="w-6 h-6 text-gray-600 hover:text-accent transition-colors" />
            </button>
            <button
              onClick={() => onShare(post)}
              className="transition-transform active:scale-90"
              data-testid={`share-btn-${post.id}`}
            >
              <Share2 className="w-6 h-6 text-gray-600 hover:text-blue-500 transition-colors" />
            </button>
          </div>
        </div>

        {/* Metrics */}
        <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
          {settings.show_likes && (
            <span className="font-semibold text-navy-900" data-testid={`likes-${post.id}`}>
              {post.likes || 0} {post.likes === 1 ? "like" : "likes"}
            </span>
          )}
          {settings.show_views && (
            <span className="flex items-center gap-1" data-testid={`views-${post.id}`}>
              <Eye className="w-3.5 h-3.5" />
              {post.views || 0}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="font-bold text-navy-900 mb-1">{post.title}</h3>

        {/* Description with read more/less */}
        {post.description && (
          <div className="text-sm text-gray-600 leading-relaxed">
            {isLong && !expanded ? (
              <>
                {post.description.slice(0, DESC_LIMIT)}...{" "}
                <button
                  onClick={() => setExpanded(true)}
                  className="text-gray-400 hover:text-navy-900 font-medium transition-colors"
                  data-testid={`read-more-${post.id}`}
                >
                  Read more
                </button>
              </>
            ) : (
              <>
                {post.description}
                {isLong && (
                  <>
                    {" "}
                    <button
                      onClick={() => setExpanded(false)}
                      className="text-gray-400 hover:text-navy-900 font-medium transition-colors"
                      data-testid={`read-less-${post.id}`}
                    >
                      Read less
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Timestamp */}
        <p className="text-xs text-gray-400 mt-2">
          {post.created_at ? new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
        </p>
      </div>
    </motion.div>
  );
}

/* ═══ Main Page ═══ */
export default function FeaturedProductsPage() {
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState({ show_views: true, show_likes: true });
  const [loading, setLoading] = useState(true);
  const [inquiryPost, setInquiryPost] = useState(null);
  const [sharePost, setSharePost] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewingImage, setViewingImage] = useState(null);
  const [sortBy, setSortBy] = useState("latest");

  useEffect(() => {
    fetchPosts();
    fetchSettings();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/showcase/products`);
      setPosts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/showcase/settings`);
      setSettings(res.data);
    } catch (e) { console.error(e); }
  };

  const handleLike = async (postId, action) => {
    try {
      const res = await axios.post(`${API}/showcase/products/${postId}/like`, { action });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: res.data.likes } : p));
    } catch (e) { console.error(e); }
  };

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!inquiryForm.name || !inquiryForm.email || !inquiryForm.message) {
      toast.error("Please fill all fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/showcase/products/${inquiryPost.id}/inquiry`, {
        ...inquiryForm,
        product_id: inquiryPost.id,
      });
      toast.success("Message sent! We'll get back to you soon.");
      setInquiryPost(null);
      setInquiryForm({ name: "", email: "", message: "" });
    } catch (e) { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const shareUrl = sharePost ? `${SITE_URL}/featured-products?post=${sharePost.id}` : "";
  const shareText = sharePost ? `Check out "${sharePost.title}" from Great Messiah Publication!` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Failed to copy"); }
  };

  const filtered = posts.filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (p.title || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q);
  }).sort((a, b) => {
    if (sortBy === "trending") return (b.likes || 0) - (a.likes || 0);
    if (sortBy === "most_viewed") return (b.views || 0) - (a.views || 0);
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="featured-products-page">
      {/* Hero */}
      <section className="bg-navy-900 py-16 relative overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_596d7fd9-5b71-4e0a-b52b-9733ffe4b361/artifacts/s9pm6mdw_image.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <ShoppingBag className="w-4 h-4 text-accent" />
              <span className="text-sm text-white/90 font-medium">Our Showcase</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="showcase-heading">
              Featured Products
            </h1>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Discover our curated collection of books, workbooks, and educational materials designed to support student excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search Bar & Sort */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative w-full sm:max-w-md" data-testid="search-bar">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-11 rounded-full bg-white border-gray-200 focus:border-accent focus:ring-accent/20"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center bg-white border border-gray-200 rounded-full p-1" data-testid="sort-toggle">
            <button
              onClick={() => setSortBy("latest")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${sortBy === "latest" ? "bg-navy-900 text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="sort-latest"
            >
              <Clock className="w-3.5 h-3.5" />
              Latest
            </button>
            <button
              onClick={() => setSortBy("trending")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${sortBy === "trending" ? "bg-accent text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="sort-trending"
            >
              <TrendingUp className="w-3.5 h-3.5" />
              Trending
            </button>
            <button
              onClick={() => setSortBy("most_viewed")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${sortBy === "most_viewed" ? "bg-accent text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="sort-most-viewed"
            >
              <Eye className="w-3.5 h-3.5" />
              Most Viewed
            </button>
          </div>
        </div>
      </div>

      {/* Masonry Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl animate-pulse mb-5 break-inside-avoid" style={{ height: 200 + Math.random() * 200 }} />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
            {filtered.map((post, index) => (
              <AnimatedSection key={post.id} delay={index * 50}>
                <PostCard
                  post={post}
                  settings={settings}
                  onLike={handleLike}
                  onInquiry={setInquiryPost}
                  onShare={setSharePost}
                  onImageClick={setViewingImage}
                />
              </AnimatedSection>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500 mb-2">No posts yet</h3>
            <p className="text-gray-400">Check back soon for updates!</p>
          </div>
        )}
      </section>

      {/* ═══ Inquiry Modal ═══ */}
      <Dialog open={!!inquiryPost} onOpenChange={() => setInquiryPost(null)}>
        <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden" data-testid="inquiry-modal">
          <div className="bg-navy-900 text-white p-6">
            <DialogHeader>
              <DialogTitle className="text-white text-lg">Send a Message</DialogTitle>
            </DialogHeader>
            {inquiryPost && (
              <div className="flex items-center gap-3 mt-3">
                <img src={resolveImageUrl(inquiryPost.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <p className="font-medium text-sm line-clamp-1">{inquiryPost.title}</p>
              </div>
            )}
          </div>
          <form onSubmit={handleInquiry} className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Your Name *</Label>
              <Input value={inquiryForm.name} onChange={e => setInquiryForm(p => ({ ...p, name: e.target.value }))} placeholder="Kwame Asante" required className="rounded-xl h-11" data-testid="inquiry-name" />
            </div>
            <div className="space-y-2">
              <Label>Your Email *</Label>
              <Input type="email" value={inquiryForm.email} onChange={e => setInquiryForm(p => ({ ...p, email: e.target.value }))} placeholder="kwame@example.com" required className="rounded-xl h-11" data-testid="inquiry-email" />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea value={inquiryForm.message} onChange={e => setInquiryForm(p => ({ ...p, message: e.target.value }))} placeholder="I'm interested in this product..." required rows={4} className="rounded-xl" data-testid="inquiry-message" />
            </div>
            <Button type="submit" disabled={sending} className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-white font-semibold" data-testid="inquiry-submit">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ═══ Share Modal — Multi-Platform ═══ */}
      <Dialog open={!!sharePost} onOpenChange={() => setSharePost(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="share-modal">
          <DialogHeader>
            <DialogTitle>Share This Post</DialogTitle>
          </DialogHeader>
          {sharePost && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                <img src={resolveImageUrl(sharePost.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <p className="font-medium text-sm line-clamp-2">{sharePost.title}</p>
              </div>

              {/* Social platforms */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank")}
                  className="rounded-xl bg-green-600 hover:bg-green-700 text-white h-11"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank")}
                  className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white h-11"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  Facebook
                </Button>
                <Button
                  onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank")}
                  className="rounded-xl bg-black hover:bg-gray-800 text-white h-11"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-4 h-4 mr-2" />
                  X / Twitter
                </Button>
                <Button
                  onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank")}
                  className="rounded-xl bg-blue-700 hover:bg-blue-800 text-white h-11"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  LinkedIn
                </Button>
              </div>

              {/* Copy link */}
              <div className="flex gap-2">
                <Input value={shareUrl} readOnly className="rounded-xl text-sm bg-gray-50" />
                <Button onClick={copyLink} variant="outline" className="shrink-0 rounded-xl" data-testid="share-copy-btn">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ Image Lightbox ═══ */}
      <AnimatePresence>
        {viewingImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm cursor-pointer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setViewingImage(null)}
            data-testid="image-lightbox"
          >
            <button
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
              onClick={() => setViewingImage(null)}
              data-testid="lightbox-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.img
              src={resolveImageUrl(viewingImage.image_url)}
              alt={viewingImage.title}
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg cursor-default"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              data-testid="lightbox-image"
            />
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium bg-black/40 px-4 py-2 rounded-full">
              {viewingImage.title}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
